import { Component, inject, Input } from '@angular/core';
import { ColDef, IServerSideGetRowsRequest } from 'ag-grid-community';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import {
  AgDateRangeFilterComponent,
  gridColumnHeader,
} from '@sensika/ag-grid-kit';
import { DataGridHostBase } from 'src/app/ag-grid/data-grid-host-base';
import { NomObjectClass } from 'src/app/core/constants';
import { OBJECT_SETTINGS_ACCESS_LEVEL } from 'src/app/core/object-settings-access-level.constants';
import { ObjectEditorService } from 'src/app/core/services/object-editor.service';
import { UtilsLogApiService } from 'src/app/api/services/utils-log-api.service';

@Component({
  selector: 'app-user-search-logs',
  templateUrl: './user-search-logs.component.html',
  styleUrls: ['./user-search-logs.component.scss'],
  standalone: false,
})
export class UserSearchLogsComponent extends DataGridHostBase {
  /** i18n scanner K4: registers 'user-search-logs.grid.column.*' as used. */
  protected readonly i18nViewName = 'user-search-logs';

  /** Set when embedded as a user-editor dialog tab; scopes the log to that user. */
  @Input() userId: string;

  private readonly utilsLogApi = inject(UtilsLogApiService);
  private readonly editService = inject(ObjectEditorService);

  readonly viewId = ViewId.SearchLog;
  readonly title = this.translateService.instant(
    'user-search-logs.title.user-search-logs',
  );
  readonly icon: IconProp = ['fas', 'search'];

  readonly fetchRows = (request: IServerSideGetRowsRequest) =>
    this.utilsLogApi.getUserSearchLogsAgGridModel(request, this.userId);

  readonly columnDefs: ColDef[] = this.buildColumnDefs();

  private columnHeader(key: string): string {
    return gridColumnHeader(this.translateService, this.i18nViewName, key);
  }

  private buildColumnDefs(): ColDef[] {
    return [
      {
        field: 'time.absolute',
        headerName: this.columnHeader('date-time-absolute'),
        filter: AgDateRangeFilterComponent,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'time.relative',
        headerName: this.columnHeader('date-time-relative'),
        tooltipValueGetter: (params) => params.data?.time?.absolute ?? '',
        minWidth: 120,
        maxWidth: 150,
      },
      {
        field: 'company.name',
        headerName: this.columnHeader('company-name'),
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'company',
          clickAccessLevel:
            OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.COMPANY],
          onClick: (company) => this.editService.editCompany(company?.id),
        },
        cellStyle: { overflow: 'visible' },
        maxWidth: 250,
      },
      {
        field: 'user.name',
        headerName: this.columnHeader('user-name'),
        filter: 'agTextColumnFilter',
        maxWidth: 280,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'user',
          clickAccessLevel: OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.USER],
          onClick: (user) => this.editService.editUser(user.id),
        },
        cellStyle: { overflow: 'visible' },
        autoHeight: true,
      },
      {
        field: 'user.email',
        headerName: this.columnHeader('user-email'),
        filter: 'agTextColumnFilter',
        maxWidth: 280,
      },
      {
        field: 'query.name',
        headerName: this.columnHeader('object-name'),
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'query',
          // Only refs with a UI target (URL or settings dialog) are links.
          clickable: (ref) => this.editService.canOpenDataObjectRef(ref),
          onClick: (ref) => this.editService.openDataObjectRef(ref),
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        field: 'query.type',
        headerName: this.columnHeader('object-type'),
        maxWidth: 180,
      },
      {
        field: 'query.human_query',
        headerName: this.columnHeader('search-context'),
        filter: 'agTextColumnFilter',
      },
      {
        field: 'action',
        headerName: this.columnHeader('action'),
        filter: 'agTextColumnFilter',
        maxWidth: 160,
      },
    ];
  }
}
